/* Anmeldung über OIDC (Single Sign-on) und Passkeys – direkt gegen die Better-Auth-Endpunkte */
import { browserSupportsWebAuthn, browserSupportsWebAuthnAutofill, startAuthentication, startRegistration, WebAuthnError } from '@simplewebauthn/browser';
import type { PasskeyInfo } from '@kaba/shared';
import { api } from './api';
import { queryClient } from './board';

export const passkeysSupported = () => typeof window !== 'undefined' && browserSupportsWebAuthn();

/** Leitet zum Anmeldedienst weiter; zurück geht es auf callbackURL */
export async function signInWithOidc(callbackURL = '/') {
  const res = await api.post<{ url?: string }>('/api/auth/sign-in/social', {
    provider: 'oidc',
    callbackURL,
    errorCallbackURL: '/?login_error=oidc_failed',
  });
  if (!res.url) throw new Error('Der Anmeldedienst ist gerade nicht erreichbar.');
  location.href = res.url;
}

/** Konto mit dem Anmeldedienst verbinden (Profil) */
export async function linkOidc() {
  const res = await api.post<{ url?: string }>('/api/auth/link-social', { provider: 'oidc', callbackURL: '/profil' });
  if (res.url) location.href = res.url;
}

function cancelled(err: unknown) {
  return err instanceof WebAuthnError ? err.code === 'ERROR_CEREMONY_ABORTED' : (err as Error)?.name === 'NotAllowedError' || (err as Error)?.name === 'AbortError';
}

/** Anmelden mit Passkey. autoFill = über die Vorschläge im E-Mail-Feld (wartet still) */
export async function signInWithPasskey(autoFill = false): Promise<boolean> {
  if (autoFill && !(await browserSupportsWebAuthnAutofill())) return false;
  const options = await api.get<Parameters<typeof startAuthentication>[0]['optionsJSON']>('/api/auth/passkey/generate-authenticate-options');
  let response;
  try {
    response = await startAuthentication({ optionsJSON: options, useBrowserAutofill: autoFill });
  } catch (err) {
    // Vorschläge im Eingabefeld: still bleiben, wenn nichts daraus wird
    if (autoFill || cancelled(err)) return false;
    throw new Error('Der Passkey konnte nicht verwendet werden.');
  }
  const { clientExtensionResults: _c, ...body } = response;
  try {
    await api.post('/api/auth/passkey/verify-authentication', { response: body });
  } catch {
    throw new Error('Dieser Passkey ist hier nicht (mehr) hinterlegt.');
  }
  await queryClient.invalidateQueries({ queryKey: ['me'] });
  return true;
}

export async function registerPasskey(name: string): Promise<boolean> {
  const options = await api.get<Parameters<typeof startRegistration>[0]['optionsJSON']>(
    `/api/auth/passkey/generate-register-options?name=${encodeURIComponent(name)}`,
  );
  let response;
  try {
    response = await startRegistration({ optionsJSON: options });
  } catch (err) {
    if (err instanceof WebAuthnError && err.code === 'ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED') throw new Error('Dieses Gerät ist schon als Passkey hinterlegt.');
    if (cancelled(err)) return false;
    throw new Error('Der Passkey konnte nicht angelegt werden.');
  }
  const { clientExtensionResults: _c, ...body } = response;
  await api.post('/api/auth/passkey/verify-registration', { response: body, name });
  return true;
}

interface RawPasskey {
  id: string;
  name?: string | null;
  createdAt?: string | null;
  deviceType: string;
  backedUp: boolean;
}

export async function listPasskeys(): Promise<PasskeyInfo[]> {
  const rows = await api.get<RawPasskey[]>('/api/auth/passkey/list-user-passkeys');
  return rows.map((p) => ({ id: p.id, name: p.name ?? null, createdAt: p.createdAt ?? null, deviceType: p.deviceType, backedUp: p.backedUp }));
}

export const deletePasskey = (id: string) => api.post('/api/auth/passkey/delete-passkey', { id });

export async function listAccounts(): Promise<string[]> {
  const rows = await api.get<{ providerId: string }[]>('/api/auth/list-accounts');
  return rows.map((r) => r.providerId);
}

/** Fehlercodes aus der Weiterleitung (?login_error=…) */
export function loginErrorText(code: string | null): string | null {
  if (!code) return null;
  switch (code) {
    case 'oidc_no_account':
      return 'Für diese Adresse gibt es hier noch kein Konto. Bitte lass dich zuerst zu einem Board einladen.';
    case 'oidc_domain':
      return 'Mit dieser E-Mail-Adresse ist die Anmeldung hier nicht erlaubt.';
    default:
      return 'Die Anmeldung über den Anmeldedienst hat nicht geklappt. Bitte versuch es noch einmal oder melde dich mit Passwort an.';
  }
}
